import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import { environment } from "../../../../environments/environment";
import { ParamsService } from "../../../../shared/Services/params.service";
import { FiduciaryUtilsService } from "../Services/fiduciary-utils.service";
import {
  FilesMetadataResponse,
  BlobFile,
  RequestFiles,
  WaitingForInformationStatusMessage,
  FiduciaryDocuments,
} from "../Types/files/request-files.interface";
import { TrustRequest } from "../Types/request.interface";

const { enlineaBackendUrl } = environment;

@Injectable()
export class FiduciaryRepository {
  constructor(
    private readonly http: HttpClient,
    private readonly paramsService: ParamsService,
    private readonly fiduciaryUtilsService: FiduciaryUtilsService
  ) {}

  private setHttpHeadersContentType(): HttpHeaders {
    const headers = new HttpHeaders();
    headers.set("Content-Type", "application/json");
    return headers;
  }

  private removeProperties(object: object, exludeKeys: string[]): object {
    const keys: string[] = Object.keys(object);
    const currentKeys: string[] = keys.filter((x) => !exludeKeys.includes(x));
    const newObject: object = {};

    currentKeys.forEach((key) => (newObject[key] = object[key]));
    return newObject;
  }

  public subirDocumentos(requestFiles: RequestFiles): Observable<FilesMetadataResponse> {
    const formData: FormData = new FormData();
    const hasFiles: boolean = requestFiles.files.length > 0;
    const userGuid: string = hasFiles ? requestFiles.files[0].creator.guid : requestFiles.user?.userFbaId;
    let userName: string = hasFiles ? requestFiles.files[0].creator.name : requestFiles.user?.nombreCompleto;
    userName = (userName ?? "").trim();
    userName = userName !== "" ? userName : requestFiles.user?.email;

    if (!hasFiles) formData.append("files", `[]`);

    const processId: string = (requestFiles.request?.processId ?? 0).toString().trim();

    const metadata: {
      requestNumber: string;
      subCaseNumber: string;
      userGuid: string;
      userName: string;
      comment: string;
      commentBase64: string;
      observation: string;
      observationBase64: string;
      processId: string;
      label: string;
    } = {
      requestNumber: requestFiles.request.requestNumber,
      subCaseNumber: requestFiles.request.subCaseNumber,
      userGuid,
      userName,
      comment: requestFiles.comment,
      commentBase64: this.fiduciaryUtilsService.stringToBase64String(requestFiles.comment),
      observation: requestFiles.observation,
      observationBase64: this.fiduciaryUtilsService.stringToBase64String(requestFiles.observation),
      processId,
      label: requestFiles.label,
    };

    const indexTags = this.removeProperties(metadata, [
      "comment",
      "commentBase64",
      "observation",
      "observationBase64",
      "processId",
      "label",
    ]);

    formData.append("metadata", JSON.stringify(metadata));
    formData.append("indexTags", JSON.stringify(indexTags));
    formData.append("caseId", processId);
    formData.append("continueCase", requestFiles.continueCase.toString());

    requestFiles.files.forEach((file) => formData.append("files", file.file));

    const headers = this.setHttpHeadersContentType();
    return this.http.post<FilesMetadataResponse>(
      `${enlineaBackendUrl}/api/fiduciaria/solicitudes/documentos/adjuntar`,
      formData,
      {
        headers,
      }
    );
  }

  public obtenerComentarios(request: TrustRequest): Observable<FiduciaryDocuments[]> {
    const params = this.paramsService.mapRequestParams({
      requestNumber: request.requestNumber,
    });

    const headers = this.setHttpHeadersContentType();
    return this.http.get<FiduciaryDocuments[]>(`${enlineaBackendUrl}/api/fiduciaria/solicitudes/documentos`, { params, headers });
  }

  public obtenerDocumentos(requestFiles: RequestFiles): Observable<BlobFile[]> {
    const params = this.paramsService.mapRequestParams({
      requestNumber: requestFiles.request.requestNumber,
    });

    const headers = this.setHttpHeadersContentType();
    return this.http.get<BlobFile[]>(`${enlineaBackendUrl}/api/fiduciaria/solicitudes/documentos/consultar`, { params, headers });
  }

  public getRequestDetails(caseId: number): Observable<WaitingForInformationStatusMessage> {
    return this.http.get<WaitingForInformationStatusMessage>(`${enlineaBackendUrl}/api/bizagi/Fiduciary/CaseData/${caseId}`);
  }
}
